import { useEffect, useState, useSyncExternalStore } from "react"
import { router, usePage } from "@inertiajs/react"
import { URL_REPLACED_EVENT } from "@/lib/replaceUrl"

export interface Bookmark {
  id: string
  url: string
  name: string
  description: string | null
}

// The newest list this tab knows of: from the last bookmark change, or from the
// last page the server sent. Back/Forward pages come from Inertia's history
// cache with the list as it was then, so a just-added bookmark would vanish
// from the bar; the bar shows this list instead once there is one. Module
// scope, so it survives the layout remounting on every page; a full reload
// clears it and starts over from fresh server props.
let latest: Bookmark[] | null = null
const listeners = new Set<() => void>()

export function setBookmarks(list: Bookmark[]) {
  latest = list
  listeners.forEach((l) => l())
}

if (typeof window !== "undefined") {
  router.on("success", (e) => {
    const list = e.detail?.page?.props?.bookmarks as Bookmark[] | undefined
    if (list) setBookmarks(list)
  })
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function useBookmarks(): Bookmark[] {
  const fromServer = (usePage().props as { bookmarks?: Bookmark[] }).bookmarks ?? []
  const local = useSyncExternalStore(subscribe, () => latest, () => null)
  return local ?? fromServer
}

// The page's address as a bookmark stores it: path plus filters, with the
// filters sorted by name so the same view always gives the same address
// (pages add filters in whatever order they were picked). Filters that repeat
// a name keep their order. The #part is left out.
export function currentAddress(): string {
  const params = [...new URLSearchParams(window.location.search)]
  params.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  const query = new URLSearchParams(params).toString()
  return query ? `${window.location.pathname}?${query}` : window.location.pathname
}

// Keeps currentAddress() fresh: page changes, Back/Forward, and filters that
// rewrite the address in place (replaceUrl).
export function useCurrentAddress(): string {
  const { url } = usePage()
  const [address, setAddress] = useState(currentAddress)

  useEffect(() => {
    const update = () => setAddress(currentAddress())
    update()
    window.addEventListener("popstate", update)
    window.addEventListener(URL_REPLACED_EVENT, update)
    return () => {
      window.removeEventListener("popstate", update)
      window.removeEventListener(URL_REPLACED_EVENT, update)
    }
  }, [url])

  return address
}
